import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@habits/shared";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Habits auth wrapper.
 *
 * NOTE (#13 — de-clone): Habits deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + glass `AuthCard` shell every clone
 * inherited). Instead each screen owns its own `AuthScene` — a full-bleed
 * streak-strip brand banner with the form laid out flat on the ambient
 * background. So this route is a thin wrapper: it only owns the "already
 * authenticated → redirect" gate and the page transition. The background, brand
 * banner and scroll container live in `AuthScene`, rendered per-screen.
 */
// MODULE scope on purpose (not inside `beforeLoad`): oxlint's
// `unicorn/consistent-function-scoping` rule marks them as ERROR if they
// live inside the closure, even with `tsc --noEmit` green. Lint is part of
// the gate.
const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

/**
 * Synchronizes the session machine and cache with the server response.
 *
 * It ALWAYS runs when the response arrives, even if the timeout has already
 * expired and the login screen is painted: the request is not canceled. Thus,
 * if a session exists, the `BootManager` sees `SERVER_REPLY` and takes the
 * user out of login — at the cost of a flicker, never a blank screen.
 */
const syncReply = (reply: FetchResult): FetchResult => {
  const { data: replyData, error: replyError } = reply;
  // Synchronizing immediately prevents the BootManager from seeing a "pending"
  // or stale session during route transition, which causes redirect loops.
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
};

function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();
  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login or /register to ADD another account
    // while the current one stays signed in (Gmail style). The "already
    // authenticated → go home" gate must be skipped here, otherwise the
    // switcher's "Add account" would bounce straight back to the app and you
    // could never add a 2nd login.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis: this `await` must NOT be unbounded. A STALLED
    // `get-session` (held; neither 401 nor 5xx, which resolve quickly) left this
    // `beforeLoad` unresolved, the router unable to mount the route, and the ENTIRE
    // app blank — measured here: empty `innerText` at 3/6/10/15/20 s on `/login`,
    // both in dev and on the production bundle. The sibling `_app` guard already
    // had a timeout; this one lacked it because the value was a local literal
    // instead of a shared constant. Now both read
    // `SESSION_CHECK_TIMEOUT_MS`.
    //
    // The request is NOT canceled: `inFlight` stays alive and `syncReply`
    // synchronizes machine + cache when the response arrives.
    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Timeout expired or network error: the login is RENDERED. The late response
      // remains hooked above and the BootManager redirects if it contains a session.
      return;
    }

    const { data } = result;

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Better Auth returns HTTP 200 with a null body for unauthenticated requests,
    // but the OpenAPI schema types Session as non-null. Cast to match runtime reality.
    const sessionData = data as { user?: unknown } | null | undefined;
    if (sessionData?.user) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
